import { useCallback, useEffect, useRef, useState } from "react";

const LS_KEY = "erpik:terminal-muted";

/**
 * useTerminalSound — Web Audio API key-click feedback for the terminal.
 *
 *   const { muted, toggleMute, playKey } = useTerminalSound();
 *
 * Default state: MUTED. Most visitors don't want sound on a portfolio,
 * and one keystroke (N) un-mutes for the few who do. The choice is
 * persisted to localStorage so a user who explicitly turned sound on
 * doesn't lose it on the next page load.
 *
 * `playKey()` produces a short blippy square-wave envelope — enough to
 * feel mechanical, nothing close to obnoxious. Cheap: one oscillator per
 * keystroke, garbage-collected as soon as the envelope ends.
 */
export function useTerminalSound() {
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage?.getItem(LS_KEY);
    if (stored === "false") return false;
    if (stored === "true")  return true;
    return true; // default
  });

  const ctxRef = useRef(null);

  // Persist any change to mute state
  useEffect(() => {
    try {
      window.localStorage?.setItem(LS_KEY, muted ? "true" : "false");
    } catch { /* private mode, swallow */ }
  }, [muted]);

  // Tear down the audio context when the consuming component unmounts
  useEffect(() => () => {
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch { /* already closed */ }
      ctxRef.current = null;
    }
  }, []);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  const playKey = useCallback(() => {
    if (muted) return;
    if (typeof window === "undefined") return;
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        ctxRef.current = new AC();
      }
      const ctx = ctxRef.current;
      // Suspended autoplay-policy state — common on first load. Resume; if
      // it's still blocked, the call will reject silently.
      if (ctx.state === "suspended") ctx.resume();

      const now = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();

      o.type = "square";
      o.frequency.setValueAtTime(720 + Math.random() * 180, now);

      g.gain.setValueAtTime(0.06, now);
      g.gain.exponentialRampToValueAtTime(0.0008, now + 0.06);

      o.connect(g).connect(ctx.destination);
      o.start(now);
      o.stop(now + 0.07);
    } catch {
      /* AudioContext blocked or hardware refused; treat as silent */
    }
  }, [muted]);

  return { muted, toggleMute, playKey };
}
